import {
  ALLOWED_TESTERS,
  CALLABLE_NAMES,
  type RequestPasswordResetRequest,
  type RequestPasswordResetResponse,
  type SendVerificationEmailRequest,
  type SendVerificationEmailResponse,
  type UiLocale,
} from '@acs/shared';
import {
  applyActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  linkWithCredential,
  onIdTokenChanged,
  reload,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  verifyPasswordResetCode,
  type AuthCredential,
  type User,
} from 'firebase/auth';
import { call } from './callable.js';
import { auth, clearLocalData, googleProvider } from './firebase.js';

/**
 * Closed-testing access gate. While the app is public but still in testing we
 * only admit a fixed set of accounts.
 *
 * The list comes from `@acs/shared` — the same one the callables check and the
 * one `allowlist.test.ts` pins to firestore.rules — so a tester can no longer be
 * added to the rules and forgotten here. `VITE_ALLOWED_EMAILS` still overrides
 * it for a preview/staging deploy; setting it to an empty string deliberately
 * disables the gate (open sign-up), which is why an UNSET variable and an empty
 * one are treated differently.
 *
 * NOTE: this client-side check only exists to show a clean "not authorized"
 * message — it is trivially bypassable. The real enforcement boundary is
 * `isSignedIn()` in firestore/firestore.rules: an allowlisted AND verified email.
 */
const override = import.meta.env.VITE_ALLOWED_EMAILS as string | undefined;
const allowedEmails = (override === undefined ? [...ALLOWED_TESTERS] : override.split(','))
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

export function isEmailAllowed(email: string | null | undefined): boolean {
  if (allowedEmails.length === 0) return true; // gate disabled
  return !!email && allowedEmails.includes(email.trim().toLowerCase());
}

/**
 * Thrown when an account is not on the tester allowlist.
 *
 * Carries the account rather than a sentence: this is thrown from the auth
 * layer, which has no locale, so the sign-in screen renders it through the
 * catalogue (`error.notAllowed`). The `message` stays English for logs.
 */
export class NotAllowedError extends Error {
  readonly account: string | null;
  constructor(email: string | null | undefined) {
    super(`${email ?? 'This account'} isn't on the TopCall24 tester list yet.`);
    this.account = email ?? null;
    this.name = 'NotAllowedError';
  }
}

/**
 * Google sign-in hit an address that already has a password account Firebase
 * will not merge on its own (doc 88 D9 — Google is a trusted provider only for
 * `@gmail.com`). The Google credential is kept: signing in with the password
 * next links it, so both ways reach the same account. Or the person resets the
 * password — needed when they never set it, because someone else registered
 * their address first.
 */
export class NeedsLinkError extends Error {
  readonly email: string | null;
  constructor(email: string | null) {
    super('This address already has a password sign-in.');
    this.email = email;
    this.name = 'NeedsLinkError';
  }
}

/**
 * What the app knows about the signed-in person — a plain snapshot, not the
 * SDK's `User`.
 *
 * The `User` object is mutated in place by `reload()`, so React never sees a
 * change: a person who has just confirmed their address would stay on "check
 * your inbox" until a page reload. Emitting a fresh snapshot whenever a field
 * changes is what moves them on (doc 88 D10).
 */
export interface AuthUser {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  displayName: string | null;
  providerIds: readonly string[];
}

export function snapshotOf(user: User | null): AuthUser | null {
  if (!user) return null;
  return {
    uid: user.uid,
    email: user.email,
    emailVerified: user.emailVerified,
    displayName: user.displayName,
    providerIds: user.providerData.map((p) => p.providerId).sort(),
  };
}

export function sameSnapshot(a: AuthUser | null, b: AuthUser | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.uid === b.uid &&
    a.email === b.email &&
    a.emailVerified === b.emailVerified &&
    a.displayName === b.displayName &&
    a.providerIds.join(',') === b.providerIds.join(',')
  );
}

/** A Google credential waiting for its password account (see NeedsLinkError). */
let pendingGoogle: { credential: AuthCredential; email: string } | null = null;

export async function signInWithGoogle(): Promise<void> {
  let user: User;
  try {
    ({ user } = await signInWithPopup(auth, googleProvider));
  } catch (e) {
    if ((e as { code?: string }).code === 'auth/account-exists-with-different-credential') {
      const credential = GoogleAuthProvider.credentialFromError(e as Parameters<typeof GoogleAuthProvider.credentialFromError>[0]);
      const email = ((e as { customData?: { email?: string } }).customData?.email ?? '').toLowerCase();
      pendingGoogle = credential && email ? { credential, email } : null;
      throw new NeedsLinkError(email || null);
    }
    throw e;
  }
  await requireAllowed(user);
}

/**
 * Create an email/password account and email its confirmation link.
 *
 * The allowlist is checked BEFORE Firebase is asked (doc 88 D8), so a
 * stranger's attempt leaves no account behind. The confirmation email is
 * best-effort here: if it fails, the verify screen's "send again" is the retry.
 */
export async function signUpWithEmail(email: string, password: string, locale: UiLocale): Promise<void> {
  const address = email.trim().toLowerCase();
  if (!isEmailAllowed(address)) throw new NotAllowedError(address);
  await createUserWithEmailAndPassword(auth, address, password);
  await sendVerification(locale).catch(() => undefined);
}

export async function signInWithEmail(email: string, password: string): Promise<void> {
  const address = email.trim().toLowerCase();
  const { user } = await signInWithEmailAndPassword(auth, address, password);
  await requireAllowed(user);
  // A Google sign-in for this address was refused a moment ago: link it now
  // that the password has proved who this is.
  if (pendingGoogle && pendingGoogle.email === address) {
    const { credential } = pendingGoogle;
    pendingGoogle = null;
    await linkWithCredential(user, credential);
    await user.getIdToken(true);
  }
}

async function requireAllowed(user: User): Promise<void> {
  if (!isEmailAllowed(user.email)) {
    await signOut(auth);
    throw new NotAllowedError(user.email);
  }
}

const sendVerificationEmailFn = call<SendVerificationEmailRequest, SendVerificationEmailResponse>(
  CALLABLE_NAMES.sendVerificationEmail,
);
const requestPasswordResetFn = call<RequestPasswordResetRequest, RequestPasswordResetResponse>(
  CALLABLE_NAMES.requestPasswordReset,
);

/** Email the signed-in user their confirmation link — sent by our server from
 *  no-reply@topcall24.com, never by Firebase (doc 88 D3). */
export function sendVerification(locale: UiLocale): Promise<SendVerificationEmailResponse> {
  return sendVerificationEmailFn({ language: locale });
}

/** Always resolves the same way, account or not (doc 88 D5). */
export async function requestPasswordReset(email: string, locale: UiLocale): Promise<void> {
  await requestPasswordResetFn({ email: email.trim().toLowerCase(), language: locale });
}

/**
 * Re-read the signed-in user and force a new ID token. The rules read
 * `email_verified` from the TOKEN, so without the forced refresh a person who
 * has just confirmed would still be refused for up to an hour. The new token
 * fires `onIdTokenChanged`, which re-renders the app with the new snapshot.
 */
export async function refreshVerification(): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) return false;
  await reload(user);
  if (user.emailVerified) await user.getIdToken(true);
  return user.emailVerified;
}

/** Apply a "confirm your email" code from our `/auth/action` link. */
export async function applyVerifyCode(code: string): Promise<void> {
  await applyActionCode(auth, code);
  await refreshVerification();
}

/** Check a reset code and return the address it is for. */
export function checkResetCode(code: string): Promise<string> {
  return verifyPasswordResetCode(auth, code);
}

export function confirmNewPassword(code: string, password: string): Promise<void> {
  return confirmPasswordReset(auth, code, password);
}

export async function signOutUser(): Promise<void> {
  await signOut(auth);
  await wipeAndReload();
}

/**
 * Every tab, not just the one whose button was pressed, wipes the on-device
 * Firestore cache when the session ends: auth state is shared across tabs, the
 * cache is too, and it cannot be deleted while any tab still holds it open.
 * Reloading is required anyway — the wiped Firestore instance is dead.
 */
let wiping: Promise<void> | null = null;
function wipeAndReload(): Promise<void> {
  wiping ??= clearLocalData().then(() => window.location.assign('/'));
  return wiping;
}

/**
 * Subscribes to sign-in AND token changes (`onIdTokenChanged`, not
 * `onAuthStateChanged`): a forced refresh after the person confirms their
 * address changes `emailVerified` without signing anyone in or out, and the app
 * has to hear about it (doc 88 D10). Token refreshes that change nothing — one
 * an hour — are not re-emitted.
 */
export function onAuthChange(cb: (user: AuthUser | null) => void): () => void {
  // A signed-in session ending — here or in another tab — wipes the cache.
  let lastUid: string | null = null;
  let last: AuthUser | null | undefined;
  return onIdTokenChanged(auth, (user) => {
    if (lastUid && !user) void wipeAndReload();
    // Reject persisted sessions for accounts that are no longer (or never were)
    // on the allowlist — e.g. a token restored on reload after removal.
    // Not counted as a session: such an account never read any data, and a
    // reload here would wipe the sign-in screen's "not on the list" message.
    if (user && !isEmailAllowed(user.email)) {
      lastUid = null;
      void signOut(auth);
      if (last !== null) cb((last = null));
      return;
    }
    lastUid = user?.uid ?? null;
    const next = snapshotOf(user);
    if (last === undefined || !sameSnapshot(last, next)) cb((last = next));
  });
}
